import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme as styledTheme } from "styled-components";
import Select, { MultiValue, ActionMeta } from "react-select";
import { FormData, FormErrors, ShowAlertProps, SelectOption, InputProps } from "../../common/types";
import {
  options,
  getCustomStylesMultiSelect,
} from "../../../utils/CustomizedLibraries/CustomSelectInput";
import "../../../App.css";
import { capitalizeFirstLetter } from "../../../utils/UtilityFunctions";
import Squarebtn from "../Squarebtn";
import { handleBlur, validate, validateField } from "../../../utils/Validations/formValidationUtils";
import { handleFormSubmit } from "../../../utils/FormSubmit/formSubmitUtils";

export const InputStyled = styled.input<InputProps>`
  width:  ${({ $width }) => $width || "100%"};
  padding: 16px;
  border: 2px solid ${({ theme, $errorColor }) => $errorColor || theme.text};
  border-radius: 2px;
  box-sizing: border-box;
  background-color: transparent;
  font-size: 12px;
  color: ${({ theme, $color }) => $color || theme.text};

  &::placeholder {
    color: grey;
  }

  &:hover {
    background: ${({ theme, $hoverBacColor }) => $hoverBacColor || theme.circularIconBg};
    color: ${({ theme, $hoverColor }) => $hoverColor || theme.circularIconTextBg};
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.circularIconBg};
    color: ${({ theme }) => theme.circularIconTextBg};
  }

  z-index: ${({ $zIndex }) => $zIndex};
`;

 export const ErrorMessage = styled.p`
  color: red;
  font-size: 12px;
  margin: 5px 0 0;
`;

const ContactForm: React.FC<ShowAlertProps> = ({ showAlert, showLoader }) => {
  const styledComponentTheme = styledTheme();

  //initial form data
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    service: [] as MultiValue<SelectOption>,
    message: "",
  });

  //initial form errors
  const [errors, setErrors] = useState<Partial<FormErrors>>({
    name: "",
    email: "",
    service: "",
    message: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    service: false,
    message: false,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // console.log('Input Value', value);
    if (touched[name as keyof typeof touched]) {
      validateFieldWrapper(name as keyof FormData, value); 
    }
  };

  const serviceRef = useRef(formData.service);
  useEffect(() => {
    serviceRef.current = formData.service;
  }, [formData.service]);

  const handleSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    setFormData((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        service: newValue,
      };
      serviceRef.current = newValue; // Ensure the ref is updated synchronously
      if (touched["service"]) {
        validateFieldWrapper("service", newValue);
      }
      return updatedFormData;
    });
  };

  const handleBlurWrapper = (name: keyof FormData, value: string | MultiValue<SelectOption>) => {
    handleBlur(name, value, touched, setTouched, validateFieldWrapper);
  };

  const validateFieldWrapper = (name: keyof FormData, value: string | MultiValue<SelectOption>) => {
    validateField(name, value, errors, setErrors);
  };

  const validateWrapper = () => validate(formData, errors, setErrors, setTouched);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    handleFormSubmit({
      formData,
      validateWrapper,
      showLoader,
      showAlert,
      setFormData: setFormData as React.Dispatch<React.SetStateAction<FormData>>,//casting to ensure types are met 
      setTouched: setTouched as (touched: Record<keyof FormData, boolean>) => void, //casting to ensure types are met
    });
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="App-form-style">
        <div style={inputContainerStyle}>
          <label htmlFor="name">Name</label>
          <InputStyled
            type="text"
            id="name"
            name="name"
            autoComplete="name"
            placeholder="Enter your name"
            value={formData.name}
            onChange={handleChange}
            onBlur={() => handleBlurWrapper("name", formData.name)}
            $errorColor={errors.name? "red": ""}
          />
          {errors.name && (
            <ErrorMessage>{capitalizeFirstLetter(errors.name)}</ErrorMessage>
          )}
        </div>
        <div style={inputContainerStyle}>
          <label htmlFor="email">Email</label>
          <InputStyled
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            onBlur={() => handleBlurWrapper("email", formData.email)}
            $errorColor={errors.email? "red": ""}
          />
          {errors.email && (
            <ErrorMessage>{capitalizeFirstLetter(errors.email)}</ErrorMessage>
          )}
        </div>
        <div style={inputContainerStyle}>
          <label htmlFor="service">Select Service</label>
          <Select
            inputId="service"
            value={formData.service}
            onChange={handleSelectChange}
            onBlur={() => handleBlurWrapper("service", serviceRef.current)} //notice the use of serviceRef
            placeholder="Select service"
            options={options}
            styles={getCustomStylesMultiSelect(styledComponentTheme, !!errors.service)}
            isSearchable={false}
            isMulti
          />
          {errors.service && (
            <ErrorMessage>{capitalizeFirstLetter(errors.service)}</ErrorMessage>
          )}
        </div>
        <div style={inputContainerStyle}>
          <label htmlFor="message">Message</label>
          <InputStyled
            as="textarea"
            id="message"
            name="message"
            placeholder="Enter your message"
            value={formData.message}
            onChange={handleChange}
            onBlur={() => handleBlurWrapper("message", formData.message)}
            style={{ height: "150px" }}
            $errorColor={errors.message? "red": ""}
          />
          {errors.message && (
            <ErrorMessage>{capitalizeFirstLetter(errors.message)}</ErrorMessage>
          )}
        </div>
        <div style={endAlignment}>
            <Squarebtn type="submit" height="50px" width="120px">
              Submit
            </Squarebtn>
        </div>
      </form>
    </>
  );
};

export const inputContainerStyle: React.CSSProperties = {
  marginBottom: "20px",
};

export const endAlignment: React.CSSProperties = {
  display: "flex",
  justifyContent: "end",
  marginBottom: "20px",
};

export default ContactForm;
